import { useMemo } from 'react';

import { slugify } from '@sg/garnish';

import {
  type MenuContentCategory,
  type MenuContentCategoryWithSlug,
  type MenuContentOrderChannel,
  type MenuContentRestaurantData,
} from '../../MenuContent.types';

// ─────────────────────────────────────────────────────────────────────────────

export const useDerivedRestaurantData = (params: Params) => {
  const { restaurantData, orderChannel } = params;

  const { menu, hours, id: restaurantId, name: restaurantName, slug: restaurantSlug } = restaurantData; // prettier-ignore

  // ─── Derived ─────────────────────────────────────────────────────────

  const categories = useMemo(
    () => menu.categories.filter(checkIfCategoryHasProducts).map(addCategorySlug),
    [menu.categories],
  );

  const isOutpost = orderChannel === 'outpost';
  const shouldRenderHoursMessage = isOutpost && typeof hours.formatted === 'string';

  // ─────────────────────────────────────────────────────────────────────

  return {
    restaurantId,
    restaurantName,
    restaurantSlug,
    hoursText: hours.formatted,
    shouldRenderHoursMessage,
    categories,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfCategoryHasProducts(category: MenuContentCategory) {
  return category.products.length > 0;
}

function addCategorySlug(category: MenuContentCategory): MenuContentCategoryWithSlug {
  return { ...category, slug: slugify(category.name) };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  restaurantData: MenuContentRestaurantData;
  orderChannel: MenuContentOrderChannel;
};
